import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Button, Spin, Alert, Empty, Tag } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { professorAvaliacoesService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';
import AvaliacaoTurmaProfessorCard from './AvaliacaoTurmaProfessorCard';
import '../../styles/PortalProfessor.css';

const { Title, Text } = Typography;

const MENSAGEM_SEM_VINCULO =
  'Seu usuário ainda não está vinculado a um professor/equipe. Procure a administração.';

// Contrato de GET /api/professor/avaliacoes (Fase 6 do módulo): lista de
// ResumoAvaliacaoTurmaProfessorResponse, uma linha por AvaliacaoTurma da
// equipe do professor autenticado. Períodos RASCUNHO administrativo
// nunca aparecem aqui -- o backend já filtra (só ABERTO/ENCERRADO).
// Agrupamento por período é puramente visual (id_periodo), nunca altera
// os dados recebidos.
const PortalProfessorAvaliacoes = () => {
  const navigate = useNavigate();
  const [resumos, setResumos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null); // { status, message } | null

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const response = await professorAvaliacoesService.listar();
      setResumos(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setErro({ status, message: MENSAGEM_SEM_VINCULO });
      } else {
        const { message: mensagem } = buildApiErrorMessage(
          err,
          'Não foi possível carregar suas avaliações. Tente novamente.',
          { context: status === 401 ? 'session' : undefined }
        );
        setErro({ status, message: mensagem });
      }
      setResumos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Passa o resumo já carregado via router state -- evita que a página
  // da turma precise adivinhar nome_turma/nome_periodo/status_periodo a
  // partir de um endpoint que não os fornece (GET .../alunos só devolve
  // identidade + status de cada aluno). Quem chegar via URL direta (sem
  // state) simplesmente não vê o cabeçalho de contexto -- degrada sem
  // quebrar, ver PortalProfessorAvaliacaoTurma.js.
  const abrirTurma = (resumo) => {
    navigate(`/professor/avaliacoes/turmas/${resumo.id_avaliacao_turma}`, { state: { resumo } });
  };

  const periodosAgrupados = resumos.reduce((acc, resumo) => {
    const chave = resumo.id_periodo;
    if (!acc.has(chave)) acc.set(chave, []);
    acc.get(chave).push(resumo);
    return acc;
  }, new Map());

  const renderConteudo = () => {
    if (loading) {
      return (
        <div className="portal-professor__state" role="status" aria-live="polite">
          <Spin size="large" />
          <Text type="secondary">Carregando suas avaliações...</Text>
        </div>
      );
    }

    if (erro) {
      return (
        <div className="portal-professor__state">
          <Alert
            type={erro.status === 403 ? 'warning' : 'error'}
            showIcon
            message={erro.status === 403 ? 'Acesso indisponível' : 'Não foi possível carregar'}
            description={erro.message}
            style={{ maxWidth: 480, textAlign: 'left' }}
          />
          {erro.status !== 403 && (
            <Button icon={<ReloadOutlined />} onClick={carregar}>
              Tentar novamente
            </Button>
          )}
        </div>
      );
    }

    if (resumos.length === 0) {
      return (
        <div className="portal-professor__state">
          <Empty description="Nenhuma avaliação disponível no momento." />
        </div>
      );
    }

    return (
      <div className="portal-professor__list">
        {Array.from(periodosAgrupados.entries()).map(([idPeriodo, turmasDoPeriodo]) => {
          const { nome_periodo: nomePeriodo, status_periodo: statusPeriodo, data_encerramento: dataEncerramento } =
            turmasDoPeriodo[0];
          const encerrado = statusPeriodo === 'ENCERRADO';
          return (
            <div className="portal-professor__periodo-secao" key={idPeriodo}>
              <div className="portal-professor__periodo-titulo">
                <Title level={4} style={{ margin: 0 }}>
                  {nomePeriodo}
                </Title>
                <Tag color={encerrado ? 'default' : 'green'}>
                  {encerrado
                    ? 'Encerrada'
                    : dataEncerramento
                      ? `Aberta até ${dayjs(dataEncerramento).format('DD/MM/YYYY')}`
                      : 'Aberta'}
                </Tag>
              </div>
              {turmasDoPeriodo.map((resumo) => (
                <AvaliacaoTurmaProfessorCard
                  key={resumo.id_avaliacao_turma}
                  resumo={resumo}
                  onAbrir={abrirTurma}
                />
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="portal-professor">
      <div className="portal-professor__header">
        <div>
          <Title level={3} className="portal-professor__title">
            Avaliações
          </Title>
          <Text type="secondary" className="portal-professor__subtitle">
            Avaliações das suas turmas
          </Text>
        </div>
      </div>

      {renderConteudo()}
    </div>
  );
};

export default PortalProfessorAvaliacoes;
